import { useState } from 'react';
import { Suggestion } from 'modules/ai/nba/types/nbaQuery';
import { Edit24 } from 'ui/icons/action/Edit24';
import { DownArrow } from 'ui/icons/navigation/DownArrow';
import { colors } from 'ui/theme/colors';
import {
  StyledSeeMoreButton,
  StyledButtonWrapper,
  StyledPopoverBodyLink,
  StyledPopoverLinkText,
  StyledSeeMoreText,
} from 'modules/ai/nba/components/recommendedContents/RecommendedContentList.styles';
import { Card } from 'modules/ai/nba/components/card/Card';
import { CardHeader } from 'modules/ai/nba/components/card/CardHeader';
import { SelectionRadioList } from 'modules/ai/nba/components/selectionListWithRadio/SelectionRadioList';
import { AI_TERMS_OF_USE_URL } from 'modules/ai/constants';
import { MessageMedium } from 'modules/domains/campaign';
import { getMessageHeaderByType } from 'modules/ai/nba/utils/medium';

export const nbaRecommendationModalIds = {
  recommendations: 'nba-recommendations',
  seeMore: 'nba-see-more-btn',
};

export type RecommendedContentListProps = {
  suggestions: Suggestion[];
  selectedValue: string;
  onSelectValue: (selectedValue: string) => void;
  messageMedium: MessageMedium;
};

export const RecommendedContentList = ({
  onSelectValue,
  selectedValue,
  suggestions,
  messageMedium,
}: RecommendedContentListProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMoreIdeasBtnHovered, setIsMoreIdeasBtnHovered] = useState(false);

  // grab first recommendation
  const [suggestionsToDisplay, setRecommendationsToDisplay] = useState([suggestions[0]]);
  const setSelectedValueCallback = (selectedVal: string) => {
    onSelectValue(selectedVal);
  };

  const messageHeaderByType = getMessageHeaderByType(messageMedium).toLowerCase();

  const PopoverContent = (
    <div>
      <div>
        These recommendations are generated using inputs from the original campaign copy and the top
        message copy users recently engaged with.
      </div>
      <StyledPopoverLinkText>
        <StyledPopoverBodyLink
          variant="monochrome"
          size="caption"
          href={AI_TERMS_OF_USE_URL}
          target="_blank"
        >
          Terms
        </StyledPopoverBodyLink>
        apply with Iterable AI
      </StyledPopoverLinkText>
    </div>
  );

  if (suggestions.length <= 0) {
    return null;
  }

  return (
    <Card testId={nbaRecommendationModalIds.recommendations}>
      <CardHeader
        icon={<Edit24 color={colors.grey50} />}
        title={`Recommended ${messageHeaderByType}`}
        popoverContentTitle="About this content"
        popoverContent={PopoverContent}
      />

      <SelectionRadioList
        setSelectedValues={setSelectedValueCallback}
        recommendations={suggestionsToDisplay}
        selectedValue={selectedValue}
      />

      {!isExpanded && (
        <StyledButtonWrapper
          onMouseOver={() => {
            setIsMoreIdeasBtnHovered(true);
          }}
          onMouseOut={() => {
            setIsMoreIdeasBtnHovered(false);
          }}
        >
          <StyledSeeMoreButton
            data-test={nbaRecommendationModalIds.seeMore}
            onClick={() => {
              setIsExpanded((prevState) => !prevState);
              // set to all the recommendations
              setRecommendationsToDisplay(suggestions);
            }}
          >
            <StyledSeeMoreText data-hovered={isMoreIdeasBtnHovered}>
              {'More ideas'}
            </StyledSeeMoreText>
            <DownArrow color={isMoreIdeasBtnHovered ? colors.textPrimary : colors.textSecondary} />
          </StyledSeeMoreButton>
        </StyledButtonWrapper>
      )}
    </Card>
  );
};
